import { GetActiveGateMovementsQuery } from '@/__generated__/graphql';
import { useQuery } from '@apollo/client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { GET_ACTIVE_GATE_MOVEMENTS } from '../graphql/queries/getActiveGateMovements';
import { isFinalizedState } from '../utils/gateMovementFinalized';

export type ActiveGateMovement = GetActiveGateMovementsQuery['activeGateMovements'][number];

/** Cuánto sigue visible un movimiento después de terminar, igual que en legacy. */
export const FINISHED_MOVEMENT_RETENTION_MS = 15_000;

const EMPTY: readonly ActiveGateMovement[] = [];

/**
 * Movimientos v2 en curso, más los que terminaron hace menos de 15 s.
 *
 * La lista la refresca quien sabe que cambió (la subscription global, el foco de la pestaña y la
 * solicitud propia); acá no hay polling. Un movimiento que sale de la lista se retiene con su
 * última foto del cache solo si esa foto es terminal: una solicitud en cola retirada sin mensaje de
 * cierre desaparece al instante.
 *
 * Un error se loguea y deja la lista vacía: el banner no está bajo ningún error boundary.
 */
export const useActiveGateMovements = (): readonly ActiveGateMovement[] => {
  const { data, error } = useQuery(GET_ACTIVE_GATE_MOVEMENTS);
  const current = data?.activeGateMovements ?? EMPTY;

  const [retained, setRetained] = useState<ReadonlyMap<number, ActiveGateMovement>>(new Map());
  const previousRef = useRef<ReadonlyMap<number, ActiveGateMovement>>(new Map());
  const timersRef = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    if (error) console.error('[useActiveGateMovements] no se pudo leer la lista', error);
  }, [error]);

  useEffect(() => {
    const currentIds = new Set(current.map((movement) => movement.id));
    const timers = timersRef.current;
    const toRetain: ActiveGateMovement[] = [];

    previousRef.current.forEach((movement, id) => {
      if (!currentIds.has(id) && isFinalizedState(movement.state)) toRetain.push(movement);
    });
    previousRef.current = new Map(current.map((movement) => [movement.id, movement]));

    const reappeared = [...timers.keys()].filter((id) => currentIds.has(id));
    if (toRetain.length === 0 && reappeared.length === 0) return;

    // Si el id vuelve a la lista, gana la lista.
    reappeared.forEach((id) => {
      clearTimeout(timers.get(id));
      timers.delete(id);
    });
    toRetain.forEach((movement) => {
      clearTimeout(timers.get(movement.id));
      timers.set(
        movement.id,
        setTimeout(() => {
          timers.delete(movement.id);
          setRetained((prev) => {
            const next = new Map(prev);
            next.delete(movement.id);
            return next;
          });
        }, FINISHED_MOVEMENT_RETENTION_MS),
      );
    });
    setRetained((prev) => {
      const next = new Map(prev);
      reappeared.forEach((id) => next.delete(id));
      toRetain.forEach((movement) => next.set(movement.id, movement));
      return next;
    });
  }, [current]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  return useMemo(() => {
    if (retained.size === 0) return current;
    const currentIds = new Set(current.map((movement) => movement.id));
    return [...current, ...[...retained.values()].filter((movement) => !currentIds.has(movement.id))];
  }, [current, retained]);
};
